import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Fab, ListInput } from 'framework7-react';
import { Input as F7Input } from 'framework7-react';
import { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import { useAppContext } from '@contexts/AppContext';
import { DynamicAction } from '@shared/types/app';
import dayjs from 'dayjs';
import { AnimatePresence, motion } from 'framer-motion';
import { omitBy, uniqBy } from 'lodash';

import {
  Entertainer,
  useCurrentEntertainerQuery,
  useFetchCurrentEntertainer,
  usePrioritizedEntertainersQuery,
  useTopEntertainersQuery,
} from '@queries/EntertainerQueries';
import { FeedEvent, useEventQuery, useUnauthorizedEventsQuery } from '@queries/EventQueries';
import { FeedPost, useEntertainerFeedPostsQuery, useFetchNextPosts } from '@queries/PostQueries';
import {
  Series,
  useSeriesByTagId,
  useSeriesByTagSlug,
  useSeriesEntertainersQuery,
  useSeriesInstagramHandleLatestPostsQuery,
} from '@queries/TagQueries';
import { useUserConnectionsQuery } from '@queries/UserConnectionsQueries';
import { useCurrentUser, useInterestQuery } from '@queries/UserQueries';

import {
  useAddEntertainerToEventMutation,
  useCreateEntertainerEventMutation,
  useEntertainerJoinSeriesMutation,
} from '@mutations/EntertainerMutations';
import { useCreateEventMutation } from '@mutations/EventMutations';
import { useUpdateSeriesEntertainersMutation } from '@mutations/TagMutations';

import { useAnalytics } from '@hooks/useAnalytics';
import { useCityById } from '@hooks/useCurrentUserLocation';
import { useDailyLocalStorageReminderIsVisible } from '@hooks/useDailyLocalStorageReminderIsVisible';
import { useDebounce } from '@hooks/useDebounce';
import { useDevice } from '@hooks/useDevice';
import { useDynamicAction } from '@hooks/useDynamicAction';
import { useEventFilters } from '@hooks/useEventFilters';
import { useShareLink } from '@hooks/useShareLink';

import {
  EntertainerDashboardAction,
  useLocation,
  useNavigate,
  useParams,
  useRouter,
} from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';
import { mapStringToParagraphs } from '@utilities/mapStringToParagraphs';
import { openExternalUrl } from '@utilities/openExternalUrl';

import Button from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import { ShareLinkButton } from '@components/buttons/ShareLinkButton';
import EventCard, {
  EventActions,
  EventHeader,
  EventImage,
  PromoterHeader,
} from '@components/cards/EventCard';
import { PostCard } from '@components/cards/PostCard/PostCard';
import { VenueCard } from '@components/cards/VenueCard';
import { ChipRadioOptions } from '@components/chips/ChipRadioOptions';
import { DatePicker } from '@components/form/DatePicker';
import { DateRangePicker } from '@components/form/DateRangePicker';
import { Input } from '@components/form/Input';
import { SearchField } from '@components/form/SearchField/SearchField';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { ButtonFilter } from '@components/layout/ButtonFilter/ButtonFilter';
import { CopyText } from '@components/layout/CopyText/CopyText';
import { useDialog } from '@components/layout/Dialog/useDialog';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';
import { LinkBanner } from '@components/layout/LinkBanner/LinkBanner';
import { List, ListItemProps } from '@components/layout/List/List';
import { PopoverWrapper } from '@components/layout/Popover/PopoverWrapper';
import { PrimaryCtaCard } from '@components/layout/PrimaryCtaCard/PrimaryCtaCard';
import { Screen } from '@components/layout/Screen/Screen';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import fireIconUrl from '@screens/CommunityScreen/assets/fire.svg';
import { ShowsTabContent } from '@screens/CommunityScreen/components/ShowsTabContent';
import {
  CreateEditEventPopup,
  SubmitEventProperties,
} from '@screens/CreateEditEventScreen/CreateEditEventForm';
import { useAuthGate } from '@screens/LogInScreen/AuthGateContext';
import { useQueueEntertainerInstagramMutation } from '@screens/LogInScreen/EntertainerLoginContent';
import { useInAppNotifications } from '@screens/NotificationsScreen/NotificationsScreen';
import { EntertainerAvailabilityCard } from '@screens/booking/GigsAndTalentScreen/EntertainerAvailabilityCard';
import { RecommendTabContent } from '@screens/booking/GigsAndTalentScreen/GigsAndTalentScreen';
import { DateFilter } from '@screens/entertainer/DragKingCommunityScreen/ScheduledSocialPost';
import { MultiSelectField } from '@screens/entertainer/components/CitiesField';

import { isUserAdmin } from '../../../../../node-api/src/lib/nicdao/api/src/middleware/auth';
import { useAppSettingsQuery } from '../../../queries/AppQueries';
import { SeriesEventCard } from '../components/SeriesEventCard';
import { LineupReferralSection } from './LineupReferralSection';
import { ProjectedEarningsCard } from './ProjectedEarningsCard';
import { SeriesAdmin } from './SeriesAdmin';
import { SeriesManageEvents } from './SeriesManageEvents';
import { SeriesManageScheduledSocialPost } from './SeriesManageScheduledSocialPost';
import { SeriesPostersReviews } from './SeriesPostersReviews';
import { SeriesRsvpToEvents } from './SeriesRsvpToEvents';
import { SweepstakesCard, useSweepstakesReferralLink } from './SweepstakesCard';
import magazineSvgUrl from './magazine.svg';

const CAN_ADD_EVENTS = true;

interface SeriesScreenProps {
  tagSlug: string;
  forceBack?: boolean;
}

const EventManagementSection: FC<{ tagSlug: string; onCanAddEvent: () => void }> = ({
  tagSlug,
  onCanAddEvent,
}) => {
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const [browseIsVisible, setBrowseIsVisible] = useState(false);
  const [searchParams, setSearchParams] = useState<
    Partial<{ search: string; startDate?: string; endDate?: string }>
  >({});

  const { openConfirmDialog } = useDialog();
  const { createAuthGateHandler } = useAuthGate();
  const { userIsLoggedIn } = useAppContext();
  const { entertainer } = useCurrentEntertainerQuery();
  const fetchCurrentEntertainer = useFetchCurrentEntertainer();

  const handleSearch = () => {
    const queryString = new URLSearchParams(searchParams).toString();

    navigate(`/noauth/series/${tagSlug}/events?${queryString}`);
  };

  const handleAddEvent = () => {
    if (userIsLoggedIn && entertainer?._id) {
      onCanAddEvent();
    } else {
      track('Open Add Event Confirm Dialog');
      openConfirmDialog({
        title: 'Add An Event',
        text: 'We currently only allow entertainers to add events. Would you like to continue as an entertainer?',
        buttonOk: 'Yes',
        onConfirm: () => {
          createAuthGateHandler(async () => {
            const { entertainer: currentEntertainer } = await fetchCurrentEntertainer();

            if (currentEntertainer._id) {
              onCanAddEvent();
            } else {
              navigate('/entertainer/onboarding', {
                state: {
                  onCompletePath: `/noauth/series/${tagSlug}?addEvent=true`,
                },
              });
            }
          });
        },
      });
    }
  };

  return (
    <div className="position-relative">
      <div className="d-flex gap-2">
        <Button
          startIcon={<FontAwesomeIcon name="search" />}
          roundness="rounded"
          onClick={() => setBrowseIsVisible(true)}
        >
          Browse Events
        </Button>
        {CAN_ADD_EVENTS && (
          <Button
            startIcon={<FontAwesomeIcon name="plus" />}
            variant="outlined"
            color="neutral"
            roundness="rounded"
            onClick={handleAddEvent}
          >
            Add Event
          </Button>
        )}
      </div>

      <AnimatePresence>
        {browseIsVisible && (
          <motion.div
            className="position-absolute w-100 z-3 top-0 pb-4"
            style={{ background: '#000' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="d-grid gap-2">
              <div className="w-100">
                <Input
                  placeholder="All events"
                  onChange={(e) =>
                    setSearchParams((prevValue) => ({
                      ...prevValue,
                      search: e.target.value,
                    }))
                  }
                  onSubmit={handleSearch}
                />
              </div>
              <div className="w-100">
                <F7Input
                  type="datepicker"
                  placeholder="Any dates"
                  readonly
                  calendarParams={{ rangePicker: true, dateFormat: 'M d', minDate: new Date() }}
                  onCalendarChange={([startDateObj, endDateObj]) => {
                    const startDate = startDateObj
                      ? dayjs(startDateObj).format('YYYY-MM-DD')
                      : undefined;
                    const endDate = endDateObj ? dayjs(endDateObj).format('YYYY-MM-DD') : undefined;

                    setSearchParams((prevValue) => ({
                      ...prevValue,
                      startDate,
                      endDate,
                    }));
                  }}
                />
              </div>
              <div className="d-flex gap-2">
                <Button
                  roundness="rounded"
                  startIcon={<FontAwesomeIcon name="search" />}
                  onClick={handleSearch}
                >
                  Search Events
                </Button>
                <Button
                  roundness="rounded"
                  color="neutral"
                  variant="smooth"
                  startIcon={<FontAwesomeIcon name="times" />}
                  onClick={() => setBrowseIsVisible(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FeaturedEventsSection: FC<{
  tagId: string | undefined;
  filterTags: { value: string; label: string }[] | undefined;
  cityId: string | undefined;
  onEventClick: (eventId: string) => void;
}> = ({ tagId = '', filterTags = [], cityId = '', onEventClick }) => {
  const [eventFilter, setEventFilter] = useState<string>('all');
  const { eventsAreReady, events = [] } = useUnauthorizedEventsQuery(
    {
      cityId,
      tagIds: [tagId, ...(eventFilter !== 'all' ? [eventFilter] : [])],
      startDate: dayjs().format('YYYY-MM-DD'),
      featured: 'true',
      limit: '5',
      rsvpData: 'ignore',
    },
    Boolean(cityId && tagId),
  );

  return (
    <div className="mt-4">
      <TitleToolbar text="Featured Events ✨" size="md" />
      {!!filterTags.length && (
        <HorizontalScroll
          items={[
            <ChipRadioOptions
              value={eventFilter}
              onChange={(value) => setEventFilter(value)}
              options={[{ value: 'all', label: 'All Featured' }, ...filterTags].map(
                ({ value, label }) => ({
                  value,
                  label,
                  // showBadge: Boolean(value === 'suggested' && events?.suggested?.length),
                }),
              )}
            />,
          ]}
        />
      )}
      {eventsAreReady ? (
        <>
          {events.length ? (
            <HorizontalScroll
              items={events.map((event) => (
                <SeriesEventCard
                  key={event._id}
                  event={event}
                  onClick={() => onEventClick(event._id)}
                />
              ))}
            />
          ) : (
            <EmptyState title="No upcoming events" text="Check back later for more events" />
          )}
        </>
      ) : (
        <Skeleton height={360} />
      )}
    </div>
  );
};

const UpcomingEventsSection: FC<{
  tagId: string | undefined;
  cityId: string | undefined;
  onEventClick: (eventId: string) => void;
}> = ({ tagId = '', cityId = '', onEventClick }) => {
  const today = dayjs().format('YYYY-MM-DD');
  const { eventDateFilterOptions, eventFilters, updateDateFilter } = useEventFilters({
    dateRange: [today, today],
  });
  const [startDate, endDate] = eventFilters.dateRange;
  const { eventsAreReady, events = [] } = useUnauthorizedEventsQuery(
    {
      startDate,
      endDate,
      tagIds: [tagId],
      cityId,
      limit: '5',
      rsvpData: 'ignore',
    },
    Boolean(cityId && tagId),
  );

  const handleDateFilterChange = (value: string) => {
    const selectedOption = eventDateFilterOptions.find((option) => option.value === value);

    if (selectedOption) {
      const [startDate, endDate] = selectedOption.getRange();
      // track('Update Date Filter', {
      //   value,
      //   startDate,
      //   endDate,
      // });
      updateDateFilter([startDate, endDate]);
    }
  };

  return (
    <div className="mt-4">
      <TitleToolbar text="Upcoming Events 🎉" size="md" />
      <HorizontalScroll
        items={[
          <ChipRadioOptions
            value={eventFilters.dateFilter}
            onChange={handleDateFilterChange}
            options={eventDateFilterOptions}
          />,
        ]}
      />
      {eventsAreReady ? (
        <>
          {events.length ? (
            <HorizontalScroll
              items={events.map((event) => (
                <SeriesEventCard
                  key={event._id}
                  event={event}
                  onClick={() => onEventClick(event._id)}
                />
              ))}
            />
          ) : (
            <EmptyState title="No upcoming events" text="Check back later for more events" />
          )}
        </>
      ) : (
        <Skeleton height={360} />
      )}
    </div>
  );
};

export const UpdateEntertainerList = ({
  seriesId,
  cityId,
  entertainers,
}: {
  seriesId;
  cityId: string;
  entertainers: { _id: string }[];
}) => {
  const [entertainerIds, setEntertainerIds] = useState<string[]>(
    entertainers?.map(({ _id }) => _id) ?? [],
  );

  const { topEntertainersAreReady, topEntertainers = [] } = useTopEntertainersQuery(
    cityId,
    'dragPerformer',
    true,
  );

  const { isUpdatingSeriesEntertainers, updateSeriesEntertainersAsync } =
    useUpdateSeriesEntertainersMutation(seriesId);

  const handleSave = () => {
    updateSeriesEntertainersAsync({ entertainerIds });
  };

  return (
    <div className="d-grid gap-1 mb-2">
      {topEntertainersAreReady ? (
        <MultiSelectField
          label="Drag Performers"
          name="entertainerIds"
          allowSearch
          defaultValue={entertainerIds}
          options={topEntertainers
            .map(({ _id, title }) => ({
              value: _id,
              label: title,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))}
          onChange={setEntertainerIds}
        />
      ) : (
        <Skeleton height={64} />
      )}

      <Button
        roundness="rounded"
        color="neutral"
        variant="smooth"
        disabled={isUpdatingSeriesEntertainers}
        onClick={() => {
          handleSave();
        }}
      >
        Update List
      </Button>
    </div>
  );
};

/**
 * @deprecated Update this to just use the Calendar & List component
 */
const SeriesEventSections: FC<{
  tagSlug: string;
  series?: Series;
  onAddEvent?: () => void;
}> = ({ tagSlug, series, onAddEvent }) => {
  const { userIsLoggedIn } = useAppContext();
  const { track } = useAnalytics();
  const { navigate, back } = useRouter();

  const handleViewEvent = (eventId: string) => {
    navigate(`/events/${eventId}`);
  };

  return (
    <>
      <EventManagementSection tagSlug={tagSlug} onCanAddEvent={onAddEvent} />

      {/* <div className="mt-4">
        {userIsLoggedIn ? (
          <LinkBanner
            showHighlight
            icon={<FontAwesomeIcon name="comment" />}
            title="Live Feed"
            description={`Check out the live feed to see the latest activity in ${series?.city?.name}`}
            onClick={() => {
              track('Click Live Feed Banner');
              back('/home');
            }}
            style={{
              background: 'linear-gradient(45deg, #1283ff 0%, #d21aff 100%)',
            }}
          />
        ) : (
          <LinkBanner
            icon={
              <IconButton className="mx-auto" variant="smooth" color="neutral" size="lg">
                <FontAwesomeIcon name="calendar" />
              </IconButton>
            }
            title="Try our Booking Wizard"
            description={`Producers and entertainers—Find entertainers or open gigs in ${series?.city?.name}`}
            onClick={() => navigate(`/noauth/booking-wizard/?cityId=${series?.city?._id}`)}
          />
        )}
      </div> */}

      <FeaturedEventsSection
        tagId={series?._id}
        filterTags={series?.filterTags?.map(({ _id: value, label }) => ({
          value,
          label,
        }))}
        cityId={series?.city?._id}
        onEventClick={handleViewEvent}
      />
      <UpcomingEventsSection
        tagId={series?._id}
        cityId={series?.city?._id}
        onEventClick={handleViewEvent}
      />
    </>
  );
};

const SeriesEventSectionsNew: FC<{
  tagSlug: string;
  series?: Series;
  onAddEvent?: () => void;
}> = ({ tagSlug, series, onAddEvent }) => {
  return (
    <ShowsTabContent
      onAddEvent={onAddEvent}
      cityId={series?.city?._id}
      tagIds={series?._id ? [series?._id] : undefined}
      back={`/noauth/series/${tagSlug}`}
      hideOpenStages={series?.hideOpenStages}
      primaryEntertainerType={series?.primaryEntertainerType}
      titleOverride={{ text: 'Upcoming Events' }}
      addShowText="Add Event"
      hideEntertainerMenuButton
    />
  );
};

export const AddSeriesEventPopup: FC<{
  isVisible: boolean;
  onClose: () => void;
  seriesId: string;
  city: { _id: string; name: string };
  entertainerType?: 'dragPerformer' | 'dj' | 'comedian';
}> = ({ isVisible, onClose, seriesId, city: initialCity, entertainerType }) => {
  const { track } = useAnalytics();
  const queryClient = useQueryClient();
  const { currentUser } = useCurrentUser();
  const { entertainer } = useCurrentEntertainerQuery();
  const { appSettings } = useAppSettingsQuery();
  const { series } = useSeriesByTagId(seriesId);

  const { createEntertainerEventAsync } = useCreateEntertainerEventMutation(entertainer?._id);
  const { createEventAsync } = useCreateEventMutation();

  const handleSubmit = async (payload: SubmitEventProperties) => {
    track('Submit Event', {
      payload,
    });

    if (entertainer?._id) {
      await createEntertainerEventAsync(payload as any);
    } else {
      await createEventAsync(payload as any);
    }

    onClose();
    await queryClient.invalidateQueries({ queryKey: ['embed-events'], exact: false });
  };

  const { addEntertainerToEventAsync } = useAddEntertainerToEventMutation();
  const handleSelectedExistingEvent = async (existingEventId: string) => {
    track('Add entertainer to existing event', {
      entertainerId: entertainer?._id,
      eventId: existingEventId,
    });
    await addEntertainerToEventAsync({
      entertainerId: entertainer?._id,
      eventId: existingEventId,
    });
    onClose();
  };

  const cityOptions = useMemo(() => {
    const primaryOption = {
      value: initialCity?._id,
      label: initialCity?.name,
    };

    return uniqBy(
      [
        primaryOption,
        ...(series?.cities?.map(({ _id: value, name: label }) => ({
          value,
          label,
        })) ?? []),
      ],
      'value',
    );
  }, [series, initialCity]);

  return (
    <>
      {seriesId && (
        <CreateEditEventPopup
          key={entertainer?._id ?? 'no-entertainer'}
          isVisible={isVisible}
          entertainerType={entertainerType}
          onClose={() => {
            track('Close Add Event Popup');
            onClose();
          }}
          cityOptions={cityOptions}
          defaultValues={{
            entertainers: currentUser?.isSuperAdmin ? [] : [{ _id: entertainer?._id }],
            tagIds: [seriesId],
          }}
          onSubmit={handleSubmit}
          onSelectExistingEvent={
            appSettings?.featureFlags?.isSearchToAddEntertainerToEventEnabled && {
              text: 'Select event',
              action: handleSelectedExistingEvent,
            }
          }
        />
      )}
    </>
  );
};

export const SeriesEntertainersList: FC<{
  series: Omit<Series, 'city'>;
  city: { _id: string; name: string };
  back: string;
  joinCategories?: string[];
  joinCallback?: () => void;
  viewAsUser?: boolean;
  CardComponent?: FC<{ entertainer: Entertainer; cityId: string; back: string }>;
}> = ({
  series,
  city,
  back,
  joinCategories,
  joinCallback,
  viewAsUser,
  CardComponent = EntertainerAvailabilityCard,
}) => {
  const { track } = useAnalytics();
  const {
    isFetched: seriesEntertainersAreReady,
    entertainers: seriesEntertainers = [],
    refetch: refetchSeriesEntertainers,
  } = useSeriesEntertainersQuery(series?._id, Boolean(series?.isEntertainerSeries));
  const { entertainerJoinSeriesAsync } = useEntertainerJoinSeriesMutation();
  const fetchCurrentEntertainer = useFetchCurrentEntertainer();
  const { createAuthGateHandler } = useAuthGate();
  const { didCopy, shareLink } = useShareLink();

  const { currentUser } = useCurrentUser();
  const { entertainer } = useCurrentEntertainerQuery();
  const { navigate, back: navigateBack } = useRouter();

  const { queueEntertainerInstagramAsync } = useQueueEntertainerInstagramMutation();

  const [entertainerSearch, setEntertainerSearch] = useState<string>();
  const [debouncedEntertainerSearch] = useDebounce(entertainerSearch, 300);

  const filteredEntertainers = useMemo(() => {
    const searchValue = debouncedEntertainerSearch?.trim()?.toLowerCase();
    if (searchValue) {
      return seriesEntertainers.filter(
        ({ title }) => title && title.toLowerCase().includes(searchValue),
      );
    }

    return seriesEntertainers;
  }, [debouncedEntertainerSearch, seriesEntertainers]);

  useEffect(() => {
    const searchValue = debouncedEntertainerSearch?.trim()?.toLowerCase();

    if (searchValue) {
      track('Search Series Entertainers', { search: debouncedEntertainerSearch });
    }
  }, [debouncedEntertainerSearch]);

  const includesCurrentEntertainer = seriesEntertainers.some(({ _id }) => _id === entertainer?._id);

  // TODO: Test when they're already logged in but haven't created an entertainer yet
  const handleJoinList = async () => {
    track('Click Join List');
    createAuthGateHandler(
      async () => {
        let scopedEntertainer = entertainer;

        if (!scopedEntertainer) {
          const { entertainer: updatedEntertainer } = await fetchCurrentEntertainer();
          scopedEntertainer = updatedEntertainer;
        }

        if (scopedEntertainer) {
          await entertainerJoinSeriesAsync({ seriesId: series?._id });
          joinCallback?.();
        }
      },
      {
        entertainerCallback: (entertainerProperties) =>
          queueEntertainerInstagramAsync({
            ...entertainerProperties,
            details: {
              categories: [
                ...(entertainerProperties.details?.categories ?? []),
                ...(joinCategories ?? []),
              ],
            },
            seriesId: series?._id,
          }),
        cityId: city?._id,
        entertainerType: series?.primaryEntertainerType,
      },
    );
  };

  const handleViewProfile = () => {
    navigate(`/noauth/entertainer/${entertainer?.bookingHandle}`);
  };

  const handleShareEmptyStateClick = async () => {
    track('Search Entertainer Empty State Share');

    await shareLink({
      url: `https://my.gaggl.app/${back}`,
    });
  };

  return (
    <div>
      <div className="mb-3">
        {currentUser?.isSuperAdmin && !viewAsUser && seriesEntertainersAreReady && (
          <UpdateEntertainerList
            seriesId={series?._id}
            cityId={city?._id}
            entertainers={seriesEntertainers}
          />
        )}
        {includesCurrentEntertainer ? (
          <Button roundness="rounded" onClick={handleViewProfile}>
            View My Profile
          </Button>
        ) : (
          <Button roundness="rounded" onClick={handleJoinList}>
            Join The List
          </Button>
        )}
      </div>
      {seriesEntertainers?.length > 10 && (
        <div className="mb-3">
          <Input
            placeholder="Search entertainers..."
            onChange={(e) => setEntertainerSearch(e.target.value)}
          />
        </div>
      )}
      {seriesEntertainersAreReady ? (
        seriesEntertainers?.length ? (
          filteredEntertainers?.length ? (
            filteredEntertainers?.map((entertainer) => (
              <CardComponent
                key={entertainer._id}
                entertainer={entertainer}
                cityId={city?._id}
                back={back}
              />
            ))
          ) : (
            <EmptyState
              title="Did we miss someone?"
              text={`There are no entertainers matching "${entertainerSearch}". Share the link with them so they can join the list`}
              button={{ text: 'Share', onClick: handleShareEmptyStateClick }}
            />
          )
        ) : (
          <EmptyState
            title="No entertainers"
            text={`No one has joined the list in ${city?.name} yet. Be the first!`}
          />
        )
      ) : (
        <>
          <Skeleton height={120} />
          <Skeleton height={120} />
          <Skeleton height={120} />
          <Skeleton height={120} />
          <Skeleton height={120} />
          <Skeleton height={120} />
        </>
      )}
    </div>
  );
};

export const SeriesFeatureApplicationSection: FC<{ tagSlug: string }> = ({ tagSlug }) => {
  const navigate = useNavigate();
  return (
    <div
      className="relative"
      style={{
        background: 'rgba(255,255,255,.1)',
        borderRadius: 8,
        overflow: 'hidden',
      }}
      onClick={() => {
        navigate(`/noauth/series/${tagSlug}/apply`);
      }}
    >
      <div className="d-flex gap-2 align-items-center py-1">
        <div className="flex-shrink-0">
          <div className="ps-2" style={{ width: 60, height: 60 }}>
            <img
              src={magazineSvgUrl}
              style={{
                width: '100%',
                height: '100%',
                display: 'block',
                margin: '0 auto',
                objectFit: 'contain',
              }}
            />
          </div>
        </div>
        <div>
          <div>
            <strong>Share Look + Get Featured</strong>
          </div>
          <div style={{ opacity: 0.75, lineHeight: 1.3, fontSize: '.8rem' }}>
            <span>Share a look in just a few steps for a personal feature in a weekly post.</span>
          </div>
        </div>
        <div className="ml-auto px-3">
          <FontAwesomeIcon name="chevron-right" />
        </div>
      </div>
    </div>
  );
};

export const SeriesContributorInstructionsSection: FC<{ tagSlug: string }> = ({ tagSlug }) => {
  const navigate = useNavigate();

  return (
    <div
      className="relative"
      style={{
        background: 'rgb(255, 148, 148)',
        color: 'rgb(162, 6, 6)',
        // background: 'rgba(226, 32, 95, 0.2)',
        // color: 'rgb(226, 32, 110)',
        borderRadius: 8,
        overflow: 'hidden',
      }}
      onClick={() => {
        navigate(`/noauth/series/${tagSlug}/support-and-earn`);
      }}
    >
      <div className="d-flex gap-2 py-2 px-2">
        <div>
          <FontAwesomeIcon name="info-circle" />
        </div>
        <div>
          <div className="d-flex align-items-center gap-2">
            <strong>Support & Earn</strong>
          </div>
          <div style={{ opacity: 0.85, lineHeight: 1.3, fontSize: '.8rem' }}>
            <span>Earn up to $5 per show that you leave hype for!</span>
          </div>
          <div className="mt-2">
            <div
              className="px-3 py-1"
              style={{
                display: 'inline-block',
                opacity: 0.85,
                fontSize: '.7rem',
                border: '1px solid rgb(162, 6, 6)',
                borderRadius: 4,
              }}
            >
              Learn More
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const SeriesSuggestedSection: FC<{ cityId: string }> = ({ cityId }) => {
  const { handleDynamicAction } = useDynamicAction();

  return (
    <div
      className="relative"
      style={{
        background: 'rgba(255,255,255,.1)',
        borderRadius: 8,
        overflow: 'hidden',
      }}
      onClick={() => {
        handleDynamicAction({
          action: DynamicAction.SplashNotificationTopWeekendEvents,
          payload: {
            cityId,
          },
        });
      }}
    >
      <div className="d-flex gap-2 align-items-center py-1">
        <div className="flex-shrink-0">
          <div className="ps-3 pe-1" style={{ width: 60, height: 60 }}>
            <img
              src={fireIconUrl}
              style={{
                width: '100%',
                height: '100%',
                display: 'block',
                margin: '0 auto',
                objectFit: 'contain',
              }}
            />
          </div>
        </div>
        <div>
          <div>
            <strong>Suggested Upcoming Shows</strong>
          </div>
          <div style={{ opacity: 0.75, lineHeight: 1.3, fontSize: '.8rem' }}>
            <span>
              Looking for drag this weekend? See our suggestions based on the vibe you're looking
              for.
            </span>
          </div>
        </div>
        <div className="ml-auto px-3">
          <FontAwesomeIcon name="chevron-right" />
        </div>
      </div>
    </div>
  );
};

const TAB_OPTION_RECORD = {
  entertainers: 'Entertainers',
  postersReviews: 'Reviews',
  events: 'Shows',
  rsvpToEvents: 'RSVP',
  featured: 'Featured',
  admin: 'Admin',
};
type TabOption = keyof typeof TAB_OPTION_RECORD | 'manageLocalDragGuide' | 'manageDynamicEvents';

const TAB_OPTIONS = Object.entries(TAB_OPTION_RECORD).map(([value, label]) => ({ value, label }));

const IS_SUPPORT_AND_EARN_PROMOTED = false;

const SeriesScreenHeaderRight: FC<{
  entertainer: Entertainer | undefined;
  series: Series | undefined;
  tagSlug: string;
  currentTab: TabOption;
  onToggleSuperAdminMode: () => void;
}> = ({ entertainer, series, tagSlug, currentTab, onToggleSuperAdminMode }) => {
  const navigate = useNavigate();

  const { isNativePlatform } = useDevice();
  const { hasUnreadNotifications } = useInAppNotifications();
  const { createAuthGateHandler } = useAuthGate();
  const { currentUser } = useCurrentUser();
  const { connectionsAreReady, connections } = useUserConnectionsQuery();

  const hasPrivateDisplayName = !!currentUser?.privateDisplayName;
  const hasAvatar = !!currentUser?.avatar;
  const hasConnections = !!connections?.length;

  const getListItems = useCallback(
    ({ onClose }: { onClose: () => void }) => {
      const showProfileBadge = !hasPrivateDisplayName || !hasAvatar;

      const listItems: ListItemProps[] = [
        {
          title: 'Manage my profile',
          onClick: () => {
            navigate('/profile');
          },
          endIcon: showProfileBadge ? <Badge color="red">1</Badge> : undefined,
        },
        {
          title: 'View my connections',
          onClick: () => {
            navigate('/manage/connections');
          },
          endIcon: !hasConnections ? <Badge color="red">1</Badge> : undefined,
        },
        {
          isVisible: currentUser?.isSuperAdmin ?? false,
          title: 'Toggle Super Admin Mode',
          onClick: onToggleSuperAdminMode,
        },
      ]
        .filter(({ isVisible = true }) => isVisible)
        .map((listItem) => ({
          ...listItem,
          onClick: () => {
            onClose();
            createAuthGateHandler(() => {
              listItem.onClick();
            });
          },
        }));

      return listItems;
    },
    [currentUser?.isSuperAdmin, hasPrivateDisplayName, hasAvatar, hasConnections],
  );

  if (
    currentUser?._id &&
    connectionsAreReady &&
    currentTab === 'rsvpToEvents' &&
    !isNativePlatform()
  ) {
    return (
      <PopoverWrapper
        PopoverContent={({ onClose }) => (
          <div>
            <List listItems={getListItems({ onClose })} />
          </div>
        )}
      >
        <IconButton
          color="neutral"
          variant="smooth"
          badge={!hasPrivateDisplayName || !hasAvatar || !hasConnections}
        >
          <FontAwesomeIcon name="bars" />
        </IconButton>
      </PopoverWrapper>
    );
  }

  if (entertainer?._id && series?.shareUrl && !isNativePlatform()) {
    return (
      <IconButton
        color="neutral"
        variant="smooth"
        badge={hasUnreadNotifications || !isNativePlatform()}
        onClick={() => {
          navigate('/notifications');
        }}
      >
        <FontAwesomeIcon name="bell" />
      </IconButton>
    );
  }

  return (
    <ShareLinkButton url={series?.shareUrl ?? `https://my.gaggl.app/noauth/series/${tagSlug}`} />
  );
};

export const SeriesScreen = createScreenComponent<
  SeriesScreenProps,
  {
    addEvent?: 'true';
    joinSeries?: 'true';
    forceBack?: 'true';
    tab?: TabOption;
    titleOverride?: string;
    posterFilter?: string;
    cdg_redirect?: string;
    rsvpFilter?: string;
    dateFilter?: DateFilter;
  }
>(({ forceBack: initialForceBack, tagSlug, back, f7route }) => {
  const { isNativePlatform } = useDevice();
  const { titleOverride } = f7route.query;
  const { userIsLoggedIn } = useAppContext();
  const { currentUser } = useCurrentUser();
  const { entertainer } = useCurrentEntertainerQuery();
  const [currentTab, setCurrentTab] = useState<TabOption>(f7route.query.tab);
  const [isSuperAdminMode, setIsSuperAdminMode] = useState(false);
  const {
    isFetched: seriesIsReady,
    series,
    seriesHost,
    seriesBonusTip,
  } = useSeriesByTagSlug(tagSlug);
  const [isAddEventPopupVisible, setIsAddEventPopupVisible] = useState(
    f7route.query.addEvent === 'true',
  );
  const { navigate, back: navigateBack } = useRouter();
  const { openAlertDialog, openConfirmDialog, openVerticalButtonsDialog } = useDialog();
  const queryClient = useQueryClient();
  const { track } = useAnalytics();
  const { entertainerJoinSeriesAsync } = useEntertainerJoinSeriesMutation();
  const fetchCurrentEntertainer = useFetchCurrentEntertainer();
  const { hasUnreadNotifications } = useInAppNotifications();

  const { createAuthGateHandler } = useAuthGate();
  const { createEntertainerEventAsync } = useCreateEntertainerEventMutation(entertainer?._id);
  const { handleDynamicAction } = useDynamicAction();

  const addEventCallback = (isEntertainer: boolean) => {
    createAuthGateHandler(
      async () => {
        let scopedEntertainer = entertainer;

        if (!scopedEntertainer) {
          const { entertainer: updatedEntertainer } = await fetchCurrentEntertainer();
          scopedEntertainer = updatedEntertainer;
        }

        if (scopedEntertainer) {
          track('Open Add Event Popup');
          setIsAddEventPopupVisible(true);
        } else {
          track('Open Mention Event Dynamic Action');
          await handleDynamicAction({
            action: DynamicAction.SplashNotificationMentionEvent,
            payload: {
              cityId: series.city._id,
            },
          });
        }
      },
      isEntertainer
        ? {
            entertainerCallback: () => {},
            cityId: series?.city?._id,
            entertainerType: series?.primaryEntertainerType,
          }
        : undefined,
    );
  };

  const handleAddEvent = () => {
    track('Click Add Event');

    if (userIsLoggedIn) {
      addEventCallback(!!entertainer?._id);
    }

    openVerticalButtonsDialog({
      title: 'Add an event?',
      text: "We're glad you want to add an event. To make sure you give you the right form to, is this your event or are you suggesting an event?",
      buttons: [
        {
          text: 'Yes, this is my event',
          strong: true,
          onClick: () => {
            addEventCallback(true);
          },
        },
        {
          text: "No, I'm suggesting an event",
          onClick: () => {
            addEventCallback(false);
          },
        },
      ],
    });
  };

  const {
    isFetched: seriesEntertainersAreReady,
    entertainers: seriesEntertainers = [],
    refetch: refetchSeriesEntertainers,
  } = useSeriesEntertainersQuery(series?._id, Boolean(series?.isEntertainerSeries));

  useEffect(() => {
    if (seriesIsReady) {
      const defaultTab = series?.isEntertainerSeries ? 'entertainers' : 'events';
      setCurrentTab(f7route.query.tab ?? defaultTab);
      console.log(`Current Tab: ${f7route.query.tab ?? defaultTab}`);
      if (f7route.query.joinSeries === 'true') {
        track('Join Series Page', {
          entertainerId: entertainer?._id,
        });
        entertainerJoinSeriesAsync({ seriesId: series?._id }).then(() => {
          refetchSeriesEntertainers();
        });
      }
    }
  }, [seriesIsReady]);

  const includesCurrentEntertainer = seriesEntertainers.some(({ _id }) => _id === entertainer?._id);

  const handleViewProfile = () => {
    navigate(`/noauth/entertainer/${entertainer?.bookingHandle}`);
  };

  // const fetchNextEntertainerFeedPosts = useFetchNextPosts();
  // const [additionalPosts, setAdditionalPosts] = useState<FeedPost[]>([]);
  // const { postsAreReady, posts: initialPosts = [] } = useEntertainerFeedPostsQuery(
  //   { cityId: series?.city?._id },
  //   series?.city?._id && series?.showEntertainerFeed,
  // );
  // const posts = useMemo(() => {
  //   return [...initialPosts, ...additionalPosts];
  // }, [initialPosts, additionalPosts]);

  const tabOptions = useMemo(() => {
    if (
      series?.isEntertainerSeries ||
      series?.showRecommendedTab ||
      (currentUser?.isSuperAdmin && (series?.hasInstagramHandles || currentTab === 'admin'))
    ) {
      return TAB_OPTIONS.filter(({ value }) => {
        if (value === 'entertainers') {
          return series?.isEntertainerSeries;
        }

        if (value === 'featured') {
          return series?.showRecommendedTab;
        }

        if (value === 'admin') {
          return (
            currentUser?.isSuperAdmin && (series?.hasInstagramHandles || currentTab === 'admin')
          );
        }

        return true;
      });
    }

    return [];
  }, [series, currentUser?.isSuperAdmin]);

  const { didCopy, shareLink } = useShareLink();
  const handleShareEmptyStateClick = async () => {
    track('Search Entertainer Empty State Share');

    await shareLink({
      url: `https://my.gaggl.app/noauth/series/${tagSlug}`,
    });
  };

  const { handleShareSweepstakesReferralLink } = useSweepstakesReferralLink({
    cityId: series?.city?._id,
  });
  const { isDailyLocalStorageReminderVisible, handleDailyLocalStorageReminderAcknowledged } =
    useDailyLocalStorageReminderIsVisible('seriesInstallAppReminderLastViewedAt', 500, 'isoWeek');

  const { city: currentCity } = useCityById(series?.city?._id);
  const isLocalDragSeries =
    series?.isLocalDragSeries || (currentCity ? `${currentCity?.slug}-drag` === tagSlug : false);
  const isLocalDragKingsAndThingsSeries = currentCity
    ? `${currentCity?.slug}-drag-kings-and-things` === tagSlug
    : false;
  const isSweepstakesCardVisible = isLocalDragSeries || (isLocalDragKingsAndThingsSeries && !back);

  const handleJoinList = async () => {
    track('Click Join List');
    createAuthGateHandler(
      async () => {
        let scopedEntertainer = entertainer;

        if (!scopedEntertainer) {
          const { entertainer: updatedEntertainer } = await fetchCurrentEntertainer();
          scopedEntertainer = updatedEntertainer;
        }

        if (scopedEntertainer) {
          await entertainerJoinSeriesAsync({ seriesId: series?._id });
        }
      },
      {
        entertainerCallback: () => {},
        cityId: series?.city?._id,
      },
    );
  };

  const forceBack = initialForceBack || isNativePlatform();

  return (
    <Screen
      name="Series Screen"
      hideToolbar
      disableLocationPermissionGate
      headerProps={{
        left:
          isLocalDragSeries && !forceBack && f7route?.query?.forceBack !== 'true' ? (
            <></>
          ) : undefined,
        back: { to: back },
        title: <Screen.GagglTitle />,
        right: (
          <SeriesScreenHeaderRight
            entertainer={entertainer}
            series={series}
            tagSlug={tagSlug}
            currentTab={currentTab}
            onToggleSuperAdminMode={() => setIsSuperAdminMode((prevValue) => !prevValue)}
          />
        ),
      }}
      metaData={{
        image: isLocalDragSeries
          ? 'https://my.gaggl.app/assets/images/local-drag-community.png'
          : undefined,
        title: `${series?.title} | Gaggl`,
        description: series?.description,
      }}
      // bottomToolbar={
      //   <Screen.BottomToolbar>
      //     <Button startIcon={<FontAwesomeIcon name="plus" />} roundness="rounded">
      //       Add Event
      //     </Button>
      //   </Screen.BottomToolbar>
      // }
    >
      <Screen.Content
        maxWidth={425}
        // infiniteScroll={{
        //   hasReachedEnd: !series?.showEntertainerFeed,
        //   onLoadMore: async () => {
        //     const { posts: nextPosts = [] } = await fetchNextEntertainerFeedPosts(
        //       'entertainers',
        //       series?.city?._id,
        //     );
        //     setAdditionalPosts((prevPosts) => [...prevPosts, ...nextPosts]);
        //   },
        // }}
      >
        {seriesIsReady ? (
          <>
            <TitleToolbar
              text={
                <span
                  style={series?.title === 'Denver Queer Calendar' ? { fontSize: '1.8rem' } : {}}
                >
                  {series?.title}
                </span>
              }
              className="mb-0"
            />

            {series?.shareUrl && (
              <div className="mb-2">
                <CopyText text={series?.shareUrl} />
              </div>
            )}

            {IS_SUPPORT_AND_EARN_PROMOTED &&
              currentTab === 'postersReviews' &&
              userIsLoggedIn &&
              entertainer?._id &&
              series && (
                <div className="mb-2">
                  {/* <SeriesContributorInstructionsSection tagSlug={tagSlug} /> */}
                  <ProjectedEarningsCard series={series} tagSlug={tagSlug} />
                </div>
              )}

            <div className="mb-3">
              {series?.description &&
                mapStringToParagraphs(series?.description, {
                  collapseTopMargin: true,
                  sharedClassName: 'my-2',
                })}
            </div>

            {!!series?.primaryCtas?.length && (
              <div className="d-grid gap-2 mb-2">
                {series?.primaryCtas?.map((cta) => (
                  <PrimaryCtaCard
                    key={cta.url}
                    cta={cta}
                    track={{ event: 'Click Series Primary CTA' }}
                  />
                ))}
              </div>
            )}

            {series?.isFeatureApplicationEnabled && (
              <SeriesFeatureApplicationSection tagSlug={tagSlug} />
            )}

            {/* <div
              className="relative"
              style={{
                background: 'rgba(255,255,255,.1)',
                borderRadius: 8,
                overflow: 'hidden',
              }}
              onClick={() => {
                navigate(`/noauth/series/${tagSlug}/apply`);
              }}
            >
              <div className="d-flex gap-2 align-items-center py-2">
                <div className="flex-shrink-0">
                  <div className="ps-2" style={{ width: 36, height: 36 }}>
                    <img
                      src={fireIconUrl}
                      alt={seriesHost?.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        display: 'block',
                        margin: '0 auto',
                        objectFit: 'contain',
                      }}
                    />
                  </div>
                </div>
                <div>
                  <div>
                    <strong>Featured Events</strong>
                  </div>
                  <div style={{ opacity: 0.75, lineHeight: 1.3, fontSize: '.8rem' }}>
                    <span>We have X upcoming trending events.</span>
                  </div>
                </div>
                <div className="ml-auto px-3">
                  <FontAwesomeIcon name="chevron-right" />
                </div>
              </div>
            </div> */}

            <div className="d-grid gap-3">
              {seriesHost && (
                <div
                  className="relative"
                  style={{
                    background: 'rgba(255,255,255,.1)',
                    borderRadius: 8,
                    overflow: 'hidden',
                    marginBottom: !isSweepstakesCardVisible && !tabOptions.length ? 8 : undefined,
                  }}
                  onClick={() => {
                    navigate(seriesHost.to);
                  }}
                >
                  <div className="d-flex gap-3 align-items-center">
                    <div className="flex-shrink-0">
                      <div style={{ width: 60, height: 60 }}>
                        <img
                          src={seriesHost?.image}
                          alt={seriesHost?.title}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </div>
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: 10,
                          textTransform: 'uppercase',
                          letterSpacing: '.1rem ',
                          opacity: 0.5,
                        }}
                      >
                        <strong>Local Ambassador</strong>
                      </div>
                      <div className="fs-6">
                        <strong>{seriesHost?.title}</strong>
                      </div>
                      {seriesHost?.description && (
                        <div className="opacity-75">{seriesHost?.description}</div>
                      )}
                    </div>
                    <div className="ml-auto px-3">
                      <FontAwesomeIcon name="chevron-right" />
                    </div>
                  </div>
                </div>
              )}

              {isSweepstakesCardVisible && (
                <SweepstakesCard
                  onLogin={handleJoinList}
                  onRevisit={() => {
                    openExternalUrl('https://gaggl.app/download');
                  }}
                  onRefer={handleShareSweepstakesReferralLink}
                  onRecommend={() => {
                    navigate(EntertainerDashboardAction.MakeRec);
                  }}
                  onAddEvent={handleAddEvent}
                />
              )}
            </div>

            {!!tabOptions.length && !['postersReviews', 'rsvpToEvents'].includes(currentTab) && (
              <ButtonFilter
                options={tabOptions}
                onChange={(value) => {
                  track(`Switch to ${value} tab`);
                  setCurrentTab(value as TabOption);
                }}
                selected={currentTab}
              />
            )}

            {currentTab === 'entertainers' && (
              <SeriesEntertainersList
                series={series}
                city={series.city}
                back={`/noauth/series/${tagSlug}?tab=entertainers`}
              />
            )}

            {currentTab === 'postersReviews' && (
              <SeriesPostersReviews
                series={series}
                seriesBonusTip={seriesBonusTip}
                tagSlug={tagSlug}
                query={f7route.query}
              />
            )}

            {(currentTab === 'events' ||
              (!tabOptions.length &&
                !series?.isEntertainerSeries &&
                !['postersReviews', 'rsvpToEvents'].includes(currentTab))) &&
              (series?.showEntertainerShows ? (
                <>
                  <div className={`d-grid gap-2 ${!tabOptions.length ? 'mt-2' : ''}`}>
                    <SeriesSuggestedSection cityId={series?.city?._id} />
                    <LineupReferralSection tagSlug={tagSlug} series={series} />
                  </div>
                  <ShowsTabContent
                    onAddEvent={handleAddEvent}
                    cityId={series?.city?._id}
                    tagIds={series?._id ? [series?._id] : undefined}
                    back={`/noauth/series/${tagSlug}`}
                    hideOpenStages={series?.hideOpenStages}
                    primaryEntertainerType={series?.primaryEntertainerType}
                  />
                </>
              ) : (
                <SeriesEventSectionsNew
                  tagSlug={tagSlug}
                  series={series}
                  onAddEvent={handleAddEvent}
                />
              ))}

            {currentTab === 'rsvpToEvents' && (
              <SeriesRsvpToEvents
                cityId={series?.city?._id}
                tagIds={series?._id ? [series?._id] : undefined}
                tagSlug={tagSlug}
                back={`/noauth/series/${tagSlug}?tab=rsvpToEvents`}
                isSuperAdminMode={isSuperAdminMode}
                initialFilterValue={f7route.query?.rsvpFilter}
              />
            )}

            {/* {series?.showEntertainerFeed && (
              <>
                <TitleToolbar text="Trending Entertainers ✨" size="md" />
                {postsAreReady ? (
                  <>
                    {posts?.map((post, i) => (
                      <PostCard key={i} disableLazyLoad post={post} />
                    ))}
                  </>
                ) : (
                  <>
                    <Skeleton height={140} />
                    <Skeleton height={140} />
                    <Skeleton height={140} />
                    <Skeleton height={140} />
                    <Skeleton height={140} />
                  </>
                )}
              </>
            )} */}
            {currentTab === 'featured' && series?.showRecommendedTab && (
              <RecommendTabContent
                cityId={series?.city?._id}
                back={`/noauth/series/${tagSlug}?tab=${currentTab}`}
              />
            )}

            {currentTab === 'admin' && series && (
              <SeriesAdmin
                referrerOptions={Object.entries(series?.lineupReferrers ?? {}).map(
                  ([value, label]) => ({ value, label }),
                )}
                tagSlug={tagSlug}
                tagId={series?._id}
                city={series?.city}
                cities={series?.cities ?? []}
              />
            )}

            {currentTab === 'manageLocalDragGuide' && series && (
              <SeriesManageScheduledSocialPost
                recommendationModeEnabled
                cityId={series?.city?._id}
                dateFilter={f7route.query?.dateFilter}
              />
            )}

            {currentTab === 'manageDynamicEvents' && series && (
              <SeriesManageEvents
                tagIds={series?._id ? [series?._id] : []}
                cityId={series?.city?._id}
              />
            )}
          </>
        ) : (
          <>
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={120} />
            <Skeleton height={40} />
            <Skeleton height={120} />
          </>
        )}
      </Screen.Content>
      {series?._id && entertainer?._id && (
        <AddSeriesEventPopup
          isVisible={isAddEventPopupVisible}
          onClose={() => setIsAddEventPopupVisible(false)}
          entertainerType={series?.primaryEntertainerType}
          seriesId={series._id}
          city={series.city}
        />
      )}
    </Screen>
  );
});
